/**
 * Endpoints de **Gestión → Clientes → Mensajes**
 * (`/api/gestion/mensajes-clientes`).
 *
 * La bandeja del administrador para los mensajes que llegan desde el portal del
 * cliente. Responder crea un mensaje hijo (`message_padre_id`) y deja el hilo en
 * `respondido`; cerrarlo impide nuevas respuestas desde el panel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { BandejaMensajesCliente, FiltrosMensajesCliente } from './mensajes-clientes.types';

const CLAVE = 'gestion';

export const mensajesClientesKeys = {
  bandeja: (filtros: FiltrosMensajesCliente) =>
    [CLAVE, 'mensajes-clientes', 'bandeja', filtros] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: FiltrosMensajesCliente): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const mensajesClientesApi = {
  bandeja: (
    filtros: FiltrosMensajesCliente,
    signal?: AbortSignal,
  ): Promise<BandejaMensajesCliente> =>
    apiClient.get<BandejaMensajesCliente>(`/api/gestion/mensajes-clientes${cadenaDe(filtros)}`, {
      signal,
    }),

  marcarLeido: (id: number): Promise<{ id: number }> =>
    apiClient.patch<{ id: number }>(`/api/gestion/mensajes-clientes/${id}/leido`),

  responder: (id: number, mensaje: string): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>(`/api/gestion/mensajes-clientes/${id}/responder`, {
      body: { mensaje },
    }),

  cerrar: (id: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.patch<{ id: number; mensaje: string }>(`/api/gestion/mensajes-clientes/${id}/cerrar`),
};

/** Bandeja de mensajes de clientes con sus filtros. */
export function useBandejaMensajes(filtros: FiltrosMensajesCliente) {
  return useQuery<BandejaMensajesCliente>({
    queryKey: mensajesClientesKeys.bandeja(filtros),
    queryFn: ({ signal }) => mensajesClientesApi.bandeja(filtros, signal),
    staleTime: 30_000,
  });
}

/** Refresca la bandeja tras cualquier cambio de estado. */
function invalidarBandeja(consulta: QueryClient) {
  void consulta.invalidateQueries({ queryKey: [CLAVE, 'mensajes-clientes'] });
}

/** Marca como leído (sin responder) un mensaje del cliente. */
export function useMarcarMensajeClienteLeido() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => mensajesClientesApi.marcarLeido(id),
    onSuccess: () => invalidarBandeja(consulta),
  });
}

/** Envía la respuesta del administrador. */
export function useResponderMensajeCliente() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: number; mensaje: string }) =>
      mensajesClientesApi.responder(variables.id, variables.mensaje),
    onSuccess: () => invalidarBandeja(consulta),
  });
}

/** Cierra el hilo (ya no se responde). */
export function useCerrarMensajeCliente() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => mensajesClientesApi.cerrar(id),
    onSuccess: () => invalidarBandeja(consulta),
  });
}
